import React, {useContext} from 'react'
import PropTypes from 'prop-types'

// false outside the MomentVM editor: every other page renders live content
const ModulePreviewContext = React.createContext(false)

/**
 * Tells the modules below whether they are rendered for the MomentVM editor.
 *
 * @component
 * @param {Object} props
 * @param {boolean} [props.preview] - True inside the `/momentvm` preview.
 * @param {React.ReactNode} props.children - The modules.
 * @returns {JSX.Element}
 */
const ModulePreviewProvider = ({preview = false, children}) => (
    <ModulePreviewContext.Provider value={preview}>{children}</ModulePreviewContext.Provider>
)

ModulePreviewProvider.propTypes = {
    preview: PropTypes.bool,
    children: PropTypes.node
}

/**
 * Whether the modules below are rendered for the MomentVM editor.
 *
 * @returns {boolean} True inside the `/momentvm` preview.
 */
const useModulePreview = () => useContext(ModulePreviewContext)

/**
 * Wraps a module that only exists as editor chrome, so it renders nothing on a
 * live page.
 *
 * @param {React.ComponentType} Component - The preview rendering.
 * @returns {React.ComponentType} The module as registered in `MODULES_MAP`.
 */
const withPreviewOnly = (Component) => {
    const PreviewOnly = React.forwardRef((props, ref) =>
        useModulePreview() ? <Component {...props} ref={ref} /> : null
    )

    if (process.env.NODE_ENV !== 'production') {
        PreviewOnly.displayName = `withPreviewOnly(${Component.displayName || Component.name})`
    }

    return PreviewOnly
}

export {ModulePreviewProvider, useModulePreview, withPreviewOnly}
